import React, { useState } from 'react';

import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    FloatingPortal,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react';

import { useBreakpoints } from '@pkg/hooks';
import { useGetPortfolioLid, useLiquidIdAddItem, useLiquidIdRemoveItem } from '@pkg/hooks/liquid-id';
import { type WalletCollectiblesBalance } from '@pkg/types';

import { type CollectionChainType } from '@app/client';
import { useToggleCollectiblesMutation } from '@app/store/zustand';

import { ReactComponent as DotsSvg } from './dots.svg';

import { MenuBackdrop, MenuButton, MenuInside, MenuWrapper, TriggerButton } from './styles';

type OptionsMenuProps = {
    item: WalletCollectiblesBalance;
    chain?: CollectionChainType;
};
export const OptionsMenu = ({ item, chain }: OptionsMenuProps) => {
    const { hideTokens, showTokens } = useToggleCollectiblesMutation();
    const [open, setOpen] = useState(false);
    const { isMobile } = useBreakpoints();

    const lidItemString = `${chain}.${item.collection.toLowerCase()}.${item.token_id}`;
    const { mutate: addItem, isLoading: isLoadingAdd } = useLiquidIdAddItem();
    const { mutate: removeItem, isLoading: isLoadingRemove } = useLiquidIdRemoveItem();
    const { data: lidData, isLoading: isLoadingCurr } = useGetPortfolioLid();

    const loading = isLoadingAdd || isLoadingRemove || isLoadingCurr;
    const addedToLid = lidData?.items.some((item) => item === lidItemString);

    const handleActionItem = () => {
        setOpen(false);
        if (addedToLid) {
            removeItem(lidItemString);
        } else {
            addItem(lidItemString);
        }
    };

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement: 'bottom-end',
        middleware: [offset(5), flip({ fallbackAxisSideDirection: 'end' }), shift()],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    // const handleSelect = () => {
    //     onOpenChange(false);
    // };

    function toggleHide() {
        setOpen(false);
        const itemId = item.id;
        if (!itemId) return;

        const collectibleIds = [itemId];
        if (item.hidden) {
            void showTokens({ collectiblesIdsToShow: collectibleIds });
        } else {
            void hideTokens({ collectiblesIdsToHide: collectibleIds });
        }
    }

    const menuProps = !isMobile
        ? {
              style: floatingStyles,
              ...getFloatingProps(),
          }
        : {
              onClick: () => setOpen(false),
          };

    const menuAnimationProps = !isMobile
        ? {
              initial: { opacity: 0, y: -10 },
              animate: { opacity: 1, y: 0 },
              exit: { opacity: 0, y: -10 },
          }
        : {
              initial: { opacity: 0, y: 20 },
              animate: { opacity: 1, y: 0 },
              exit: { opacity: 0, y: 20 },
          };

    return (
        <>
            <TriggerButton ref={refs.setReference} {...getReferenceProps()} $isOpen={open}>
                <DotsSvg />
            </TriggerButton>

            {open && (
                <FloatingPortal>
                    <FloatingFocusManager context={context} modal={false}>
                        <MenuWrapper ref={refs.setFloating} {...menuProps}>
                            <MenuInside {...menuAnimationProps}>
                                <MenuButton onClick={() => toggleHide()}>
                                    {item.hidden ? 'Show on profile' : 'Hide from profile'}
                                </MenuButton>
                                <MenuButton disabled={loading} onClick={handleActionItem}>
                                    {addedToLid ? 'Remove from Liquid ID' : 'Add to Liquid ID'}
                                </MenuButton>
                                {/*<MenuButton onClick={() => handleSelect()}>Copy link</MenuButton>*/}
                            </MenuInside>
                            <MenuBackdrop />
                        </MenuWrapper>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </>
    );
};
